import React from "react";
import { formatNumber, formatMethodName } from "../../utils/formatting";
import type { OverviewResponse, BotMethodType } from "../../pages/AdminDashboard";

interface AdminOverviewTabProps {
    overview: OverviewResponse | null;
    overviewRange: "24h" | "7d" | "30d" | "90d";
    setOverviewRange: (range: "24h" | "7d" | "30d" | "90d") => void;
    loadingOverview: boolean;
    fetchOverview: () => void;
}

const AdminOverviewTab: React.FC<AdminOverviewTabProps> = ({
    overview,
    overviewRange,
    setOverviewRange,
    loadingOverview,
    fetchOverview,
}) => {
    return (
        <div className="glass-card" style={{ padding: "16px", borderRadius: "16px", display: "grid", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                    <select
                        value={overviewRange}
                        onChange={(e) => setOverviewRange(e.target.value as "24h" | "7d" | "30d" | "90d")}
                        className="input-dark"
                        style={{ padding: "8px" }}
                    >
                        <option value="24h">Last 24h</option>
                        <option value="7d">Last 7d</option>
                        <option value="30d">Last 30d</option>
                        <option value="90d">Last 90d</option>
                    </select>
                    <button
                        onClick={() => fetchOverview()}
                        style={{
                            padding: "8px 12px",
                            borderRadius: "8px",
                            border: "1px solid rgba(255,255,255,0.15)",
                            background: "rgba(255,255,255,0.06)",
                            color: "white",
                            cursor: "pointer",
                        }}
                    >
                        Refresh
                    </button>
                </div>
                {overview && (
                    <span style={{ color: "#9ca3af", fontSize: "12px" }}>
                        Range: {new Date(overview.range.start).toLocaleDateString()} - {new Date(overview.range.end).toLocaleDateString()}
                    </span>
                )}
            </div>

            {loadingOverview ? (
                <p style={{ color: "#9ca3af" }}>Loading overview...</p>
            ) : overview ? (
                <>
                    <div
                        style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: "10px",
                        }}
                    >
                        {[
                            { label: "Total Users", value: overview.kpis.totalUsers },
                            { label: "Active Users", value: overview.kpis.activeUsers },
                            { label: "New Users", value: overview.kpis.newUsers },
                            { label: "Total Bots", value: overview.kpis.totalBots },
                            { label: "Active Bots", value: overview.kpis.activeBots },
                            { label: "New Bots", value: overview.kpis.newBots },
                            { label: "Method Runs", value: overview.kpis.methodRunsInRange },
                            { label: "Fees (SOL)", value: formatNumber(overview.kpis.totalFeesSol, 4) },
                        ].map((item) => (
                            <div
                                key={item.label}
                                style={{
                                    padding: "12px",
                                    borderRadius: "12px",
                                    background: "rgba(255,255,255,0.04)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                <div style={{ color: "#9ca3af", fontSize: "12px" }}>{item.label}</div>
                                <div style={{ fontSize: "22px", fontWeight: 700, marginTop: "4px" }}>{item.value}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "10px" }}>
                        {(["volume", "inject", "extract", "defendFloor"] as BotMethodType[]).map((method) => (
                            <div
                                key={method}
                                style={{
                                    padding: "10px",
                                    borderRadius: "12px",
                                    background: "rgba(255,255,255,0.03)",
                                    border: "1px solid rgba(255,255,255,0.08)",
                                }}
                            >
                                <div style={{ color: "#9ca3af", fontSize: "12px" }}>{formatMethodName(method)} active now</div>
                                <div style={{ fontSize: "20px", fontWeight: 600 }}>{overview.methods.activeNow[method] || 0}</div>
                            </div>
                        ))}
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: "740px" }}>
                            <h3 style={{ marginBottom: "8px", fontSize: "15px" }}>Method Usage</h3>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                                        <th style={{ padding: "8px" }}>Method</th>
                                        <th style={{ padding: "8px" }}>Runs</th>
                                        <th style={{ padding: "8px" }}>Completed</th>
                                        <th style={{ padding: "8px" }}>Completion %</th>
                                        <th style={{ padding: "8px" }}>Total Value</th>
                                        <th style={{ padding: "8px" }}>Avg Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.methods.usage.map((row) => {
                                        const completion = row.runCount > 0 ? (row.completedCount / row.runCount) * 100 : 0;
                                        return (
                                            <tr key={row.methodType} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                                <td style={{ padding: "8px" }}>{formatMethodName(row.methodType)}</td>
                                                <td style={{ padding: "8px" }}>{formatNumber(row.runCount, 0)}</td>
                                                <td style={{ padding: "8px" }}>{formatNumber(row.completedCount, 0)}</td>
                                                <td style={{ padding: "8px" }}>{formatNumber(completion, 1)}%</td>
                                                <td style={{ padding: "8px" }}>{formatNumber(row.totalValue, 3)}</td>
                                                <td style={{ padding: "8px" }}>{formatNumber((row.avgDurationMs || 0) / 1000, 1)}s</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div style={{ overflowX: "auto" }}>
                        <div style={{ minWidth: "760px" }}>
                            <h3 style={{ marginBottom: "8px", fontSize: "15px" }}>Daily Volume and Runs</h3>
                            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                <thead>
                                    <tr style={{ color: "#9ca3af", textAlign: "left" }}>
                                        <th style={{ padding: "8px" }}>Day</th>
                                        <th style={{ padding: "8px" }}>Runs</th>
                                        <th style={{ padding: "8px" }}>Total Value</th>
                                        <th style={{ padding: "8px" }}>Volume</th>
                                        <th style={{ padding: "8px" }}>Inject</th>
                                        <th style={{ padding: "8px" }}>Extract</th>
                                        <th style={{ padding: "8px" }}>Defend Floor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {overview.series.daily.length === 0 && (
                                        <tr>
                                            <td style={{ padding: "8px", color: "#9ca3af" }} colSpan={7}>
                                                No method runs recorded for selected range.
                                            </td>
                                        </tr>
                                    )}
                                    {overview.series.daily.map((row) => (
                                        <tr key={row.day} style={{ borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                                            <td style={{ padding: "8px" }}>{row.day}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.runs, 0)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.totalValue, 3)}</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.methods.volume.totalValue, 3)} ({row.methods.volume.runs})</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.methods.inject.totalValue, 3)} ({row.methods.inject.runs})</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.methods.extract.totalValue, 3)} ({row.methods.extract.runs})</td>
                                            <td style={{ padding: "8px" }}>{formatNumber(row.methods.defendFloor.totalValue, 3)} ({row.methods.defendFloor.runs})</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : (
                <p style={{ color: "#9ca3af" }}>No overview data loaded yet.</p>
            )}
        </div>
    );
};

export default AdminOverviewTab;
